import "quill/dist/quill.snow.css";

import {
  Box,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Delta } from "quill";
import { useEffect, useState } from "react";
import { useQuill } from "react-quilljs";

import useGlobal from "../../hooks/useGlobal";
import { Task } from "../../types/task";
import {
  getCreateChannelChipColors,
  getOrderTypeLabel,
  getTaskTypeColor,
  getTaskTypeLabel,
} from "../../utils/constants";
import CustomChip from "../CustomChip";
import CustomDialog from "../CustomDialog";
import ShowECPTags from "../ShowECPTags";
import ShowTaskTags from "../ShowTaskTags";
import Upload from "../Upload";

type PropsType = {
  task: Task;
};

const disabledFontColor = {
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#25272c",
    WebkitTextFillColor: "#25272c",
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: "#25272c",
  },
};

const Detail = ({ task }: PropsType) => {
  const { getOrderCycleOptions } = useGlobal();
  const quillTheme = "snow";

  const modules = {
    toolbar: false,
    clipboard: { matchVisual: false },
  };

  const placeholder = "";

  const formats = ["image"];

  const { quill, quillRef } = useQuill({
    theme: quillTheme,
    modules,
    formats,
    placeholder,
    readOnly: true,
  });

  const [openAlert, setOpenAlert] = useState(false);
  const [moreImages, setMoreImages] = useState<string[]>([]);

  useEffect(() => {
    if (task?.ecp?.alert_1 && task?.ecp?.alert_1 !== "") {
      setOpenAlert(true);
    }
  }, [task?.ecp?.alert_1]);

  useEffect(() => {
    if (!quill) return;

    quill.clipboard.addMatcher("img", () => new Delta());

    quill.root.innerHTML = task?.content || "";
    setMoreImages(task?.more_images || []);
  }, [quill]);

  return (
    <>
      <Grid container spacing={"12px"}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Grid container spacing={"12px"}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="code">ECP Code Sold To</InputLabel>
                <TextField
                  placeholder="ECP Code Sold To"
                  fullWidth
                  value={task?.ecp?.code}
                  disabled
                  sx={disabledFontColor}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="name">ECP Name Sold To</InputLabel>
                <TextField
                  placeholder="ECP Name Sold To"
                  fullWidth
                  value={task?.ecp?.name}
                  disabled
                  sx={disabledFontColor}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="code">ECP Code Ship To</InputLabel>
                <TextField
                  placeholder="ECP Code Ship To"
                  fullWidth
                  value={task?.ecp_ship_to_code}
                  disabled
                  sx={disabledFontColor}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="name">ECP Name Ship To</InputLabel>
                <TextField
                  placeholder="ECP Name Ship To"
                  fullWidth
                  value={task?.ecp_ship_to_name}
                  disabled
                  sx={disabledFontColor}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="order_type">Order Type</InputLabel>
                <TextField
                  placeholder="Order Type"
                  fullWidth
                  value={getOrderTypeLabel(task?.order_type)}
                  disabled
                  sx={disabledFontColor}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="order_cycle">Order Cycle</InputLabel>
                <Select
                  displayEmpty
                  inputProps={{ readOnly: true }}
                  value={
                    getOrderCycleOptions()?.some(
                      (tag: any) => tag.value === task?.order_cycle,
                    )
                      ? task?.order_cycle
                      : ""
                  }
                  renderValue={(selected) => {
                    if (!getOrderCycleOptions()) return null;

                    if (selected === "") {
                      return (
                        <div className="text-text-disabled">Order Cycle</div>
                      );
                    }

                    const selectedTag = getOrderCycleOptions().find(
                      (tag: any) => tag.value === selected,
                    );

                    if (selectedTag) {
                      return <>{selectedTag.chip}</>;
                    }

                    return null;
                  }}
                >
                  {getOrderCycleOptions()?.map((tag: any, index: number) => (
                    <MenuItem key={`payment-${index}`} value={tag.value}>
                      {tag.chip}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="task_type">Task Type</InputLabel>
                <TextField
                  placeholder="Task Type"
                  fullWidth
                  value={getTaskTypeLabel(task?.task_type)}
                  disabled
                  sx={{
                    ...disabledFontColor,
                    "& .MuiInputBase-input.Mui-disabled": {
                      color: getTaskTypeColor(task?.task_type),
                      WebkitTextFillColor: getTaskTypeColor(task?.task_type),
                      fontWeight: 600,
                    },
                  }}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="create_channel">Create Channel</InputLabel>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "rgba(0, 0, 0, 0.23)",
                    borderRadius: 1,
                    px: "14px",
                    minHeight: "56px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {task?.create_channel === "line" || task?.create_channel === "email" ? (
                    <CustomChip
                      label={task.create_channel.toUpperCase() === "LINE" ? "LINE" : "Email"}
                      size="small"
                      {...getCreateChannelChipColors(task.create_channel)}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ color: "#25272c" }}>
                      {task?.create_channel || "-"}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="creator_ecp_code">
                  Creator ECP Code
                </InputLabel>
                <TextField
                  placeholder="Creator ECP Code"
                  fullWidth
                  value={task?.metadata?.creator_ecp_code || "-"}
                  disabled
                  sx={disabledFontColor}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="ecp_promotion_detail">
                  ECP Promotion Detail
                </InputLabel>
                <TextField
                  placeholder="ECP Promotion Detail"
                  fullWidth
                  value={task?.ecp?.detail || ""}
                  disabled
                  sx={disabledFontColor}
                  multiline
                  rows={4}
                  maxRows={8}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="ecp_alert">ECP Logistic Note</InputLabel>
                <TextField
                  placeholder="ECP Logistic Note"
                  fullWidth
                  value={task?.ecp?.note || ""}
                  disabled
                  sx={disabledFontColor}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="ecp_alert">ECP Alert</InputLabel>
                <TextField
                  placeholder="ECP Alert"
                  fullWidth
                  value={task?.ecp?.alert_1 || ""}
                  disabled
                  sx={disabledFontColor}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="order_quantity">Order QTY</InputLabel>
                <TextField
                  placeholder="Order QTY"
                  fullWidth
                  value={task?.order_quantity}
                  disabled
                  sx={disabledFontColor}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="tracking_no">Tracking No.</InputLabel>
                <TextField
                  placeholder="Tracking No."
                  fullWidth
                  value={task?.tracking_numbers?.join(", ")}
                  disabled
                  sx={disabledFontColor}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="ecp_tag">ECP Tag</InputLabel>
                <ShowECPTags ecp={task?.ecp} />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Stack useFlexGap spacing={"12px"}>
                <InputLabel htmlFor="tags_name">Tag Add</InputLabel>
                <ShowTaskTags tags={task?.tags_response} />
              </Stack>
            </Grid>
            {task?.urgent_note && (
              <Grid size={{ xs: 12, md: 12 }}>
                <Stack useFlexGap spacing={"12px"}>
                  <InputLabel htmlFor="urgent_note">Urgent Note</InputLabel>
                  <TextField
                    placeholder="Urgent Note"
                    fullWidth
                    value={task?.urgent_note}
                    disabled
                    sx={disabledFontColor}
                  />
                </Stack>
              </Grid>
            )}
            {task?.is_urgent_task && (
              <Grid size={{ xs: 12, md: 12 }}>
                <Stack useFlexGap spacing={"12px"}>
                  <InputLabel htmlFor="key_in_called_name">
                    Urgent Assign
                  </InputLabel>
                  <TextField
                    placeholder="Urgent Assign"
                    fullWidth
                    value={task?.key_in_called_name}
                    disabled
                    sx={disabledFontColor}
                  />
                </Stack>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 12 }}>
              <Upload
                files={moreImages}
                setFiles={setMoreImages}
                readOnly={true}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack
            useFlexGap
            spacing={"12px"}
            component={"div"}
            sx={{ height: "100%" }}
          >
            <InputLabel htmlFor="capture">Order Capture</InputLabel>
            <div
              style={{
                width: "100%",
                height: "100%",
              }}
              onCopy={(e) => {
                const text = window.getSelection()?.toString() ?? "";
                if (text) {
                  e.clipboardData.setData("text/plain", text);
                  e.preventDefault();
                }
              }}
            >
              <div ref={quillRef} style={{ borderRadius: "4px" }} />
            </div>
          </Stack>
        </Grid>
      </Grid>

      {openAlert && (
        <CustomDialog
          open={openAlert}
          onClose={() => setOpenAlert(false)}
          title={"Customer Alert"}
          titleStyle={{
            color: "red",
          }}
          content={
            <>
              <Typography variant="body2" color="black">
                {task?.ecp?.alert_1}
              </Typography>
            </>
          }
        />
      )}
    </>
  );
};

export default Detail;
